import { useState, useEffect, useMemo } from 'react';
import { Alert, Button, Card, Col, Form, Nav, Row, Spinner } from 'react-bootstrap';
import { dynamicTranslateOpportunityFields } from '../../../utils/opportunity'
import { getOpportunityCreationValidationSchema } from '../OpportunityCreation/OpportunityCreation';
import { Storage } from 'aws-amplify';
import { FaCheck, FaInfoCircle } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import CreatableSelect from 'react-select/creatable';
import ReactQuill from 'react-quill';
import i18next from 'i18next';
import _ from 'lodash';
import { emailIsValid, randomString, removeDuplicates, strToFloat } from '../../../utils';
import { Markets, OpportunityFunnelValues, ReactSelectOption, RemoteSelectOption } from '../../../types';
import { useApiClient } from '../../../hooks/useApiClient';
import { AccountEntitySearch, OpportunityEntity, OpportunityField } from '../../../api/models/AccountEntity';
import { VehicleEntity, OpportunityCreateRequestDTO, OpportunityUpdateRequestDTO, VehicleUpdateRequestDTO } from '../../../api';
import { OPPORTUNITY_CREATION_LEAD_SOURCE, OPPORTUNITY_STATES_DATA, OPPORTUNITY_SUPPORTED_FILE_TYPES, horizonPointId } from '../../../utils/constants';
import { OpportunityStageEnum, RoleEnum } from "../../../api/models/Enums";
import { opportunityColumnsData } from './opportunityColumnsData';
import AccountSmallPreview from '../../AccountSmallPreview/AccountSmallPreview';
import SelectOptWithSubtitle from '../../SelectOptWithSubtitle/SelectOptWithSubtitle';
import MultiFileUploader from '../../MultiFileUploader/MultiFileUploader';
import VehicleCardBody from '../../VehicleCardBody/VehicleCardBody';
import toaster from '../../../utils/toaster';
import RequiredFormLabel from '../../RequiredFormLabel/RequiredFormLabel';
import MotornetVehicleSelector, { MotornetSelectedVehicle, getMakeName } from '../../../motornet/common/MotornetVehicleSelector';
import { productionFetchParams } from '../../../motornet/common/selectorMeta';
import OpportunitiesTable from '../OpportunitiesTable/OpportunitiesTable';
import OpportunitySmallPreview from '../OpportunitySmallPreview/OpportunitySmallPreview';
import CardPlaceholder from '../../CardPlaceholder/CardPlaceholder';
import MissingAccountRecordTypeAlert from '../../MissingAccountRecordTypeAlert/MissingAccountRecordTypeAlert';
import OpportunityModals from '../OpportunityModals/OpportunityModals';
import { TiassistoVehicleSaveRequestDTO } from '../../../api/models/TiassistoModels/TiassistoVehicleSaveRequestDTO';
import { useAuth } from '../../../hooks/useAuth';
import 'react-quill/dist/quill.snow.css';
import './OpportunityFunnel.scss';
import OpportunityFunnelDriveItEasyForm from '../OpportunityFunnelDriveItEasyForm/OpportunityFunnelDriveItEasyForm';
import TiassistoElement from '../../TiAssisto/TiassistoElement';
import dayjs from 'dayjs';
import useSalesforceStore from '../../../store/SalesforceStore';
import { createVehicleSearch } from 'src/vehicleList/vehicleSearch';
import { translateOpportunityState } from '../../../utils/translations';
import { isMarket } from 'src/utils/markets';
import { OpportunityBackofficeDTO } from 'src/api/models/Opportunities/OpportunityBackofficeDTO';
import { useErrorToast } from 'src/hooks/useErrorToast';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';

type AccountSelectOpt = ReactSelectOption & { account: AccountEntitySearch; subtitle: string; };

export type FunnelStep = 'account' | 'vehicle' | 'upload_docs' | 'send_docs' | 'opportunity' | 'send_or_upload_docs'

const emailTemplate = `
	Spett.le Cliente,
	<br>
	con la presente le invio l'elenco della documentazione da produrre per poter istruire la pratica di noleggio. <br>Le chiedo di mandare tutto compilato, come da indicazioni, al suo referente commerciale.
	<br>
	In attesa di un suo gentile riscontro resto a disposizione per ogni dubbio.
	<br>
	Cordiali saluti
	<br>
	`;

const NavItem = ({ title, text, eventKey, isValid = false, mustComplete, showIsOptional = false, forceRequired = false }: {
	forceRequired?: boolean;
	showIsOptional?: boolean;
	eventKey: string;
	title: string;
	text: string;
	mustComplete: FunnelStep[];
	isValid?: boolean;
}) => {
	const isRequired = showIsOptional === true ? false : (forceRequired || mustComplete.includes(eventKey as FunnelStep));

	return (
		<Nav.Item >
			<Nav.Link eventKey={eventKey} className='text-start text-muted text-success'>
				<div>
					{isValid && <span><FaCheck className='text-success'></FaCheck> &nbsp;</span>}
					<b>
						<RequiredFormLabel label={title} required={isRequired} />
					</b>
				</div>
				<div className='text-sm'>
					{text}
				</div>
				{/* <Badge bg={mustComplete.includes(eventKey as FunnelStep) ? 'primary' : 'secondary'}>
						{mustComplete.includes(eventKey as FunnelStep) ? 'Richiesto' : ' Facoltativo'}
					</Badge> */}
			</Nav.Link>
		</Nav.Item>
	)
}

interface OpportunityFunnelProps {
	account?: AccountEntitySearch
	vehicle?: VehicleEntity
	opportunity?: OpportunityEntity
	canChange: FunnelStep[]
	mustComplete: FunnelStep[]
	startFrom: FunnelStep
	saveLabel?: string
	nextState?: OpportunityStageEnum
	onQuit?: () => void
	afterSubmitFn: (opportunityId: string) => void
}

interface FunneDataState {
	account?: AccountEntitySearch
	vehicle?: VehicleEntity
}


const OpportunityFunnel = ({ account, vehicle, canChange, saveLabel, mustComplete, startFrom, opportunity,
	nextState = OpportunityStageEnum.waiting_sign, onQuit, afterSubmitFn }: OpportunityFunnelProps) => {
	const { user, userRole, userReferencePartnerList } = useAuth();
	const baseOpportunitySchema = getOpportunityCreationValidationSchema(userRole);
	const opportunityOptionCreationSchema = baseOpportunitySchema.concat(
		Yup.object().shape({
			LeadSource: Yup.object().required(i18next.t('required_field')),
		})
	);
	const [funnelData, setFunnelData] = useState<FunneDataState>({ account, vehicle })
	const { t } = useTranslation()
	const [finalState, setFinalState] = useState<OpportunityStageEnum>(nextState as OpportunityStageEnum)
	const [documentationFolder, setDocumentationFolder] = useState<ReactSelectOption[]>([]);
	const { apiClient, handleApiResponse, handleApi } = useApiClient();
	const [loading, setLoading] = useState(true);
	const [sending, setSending] = useState(false)
	const [sendFiles, setsendFiles] = useState<File[]>([])
	const [modalType, setModalType] = useState('');
	const [sendData, setSendData] = useState<{
		enabled: boolean
		message: string
		to: ReactSelectOption | null
		dealer: ReactSelectOption | null
		dealerDocs: string[]
		cc: ReactSelectOption[]
	}>({ to: null, enabled: false, dealer: null, cc: [], message: emailTemplate, dealerDocs: [] })
	const { setAccountSalesforce, accountSalesforce } = useSalesforceStore()
	const [dealerFolderData, setDealerFolderData] = useState<any[]>([])
	const [canSave, setCanSave] = useState<boolean>(false)
	const [validation, setValidation] = useState<{
		account: boolean
		vehicle: boolean
		upload_docs: boolean
		send_docs: boolean
		opportunity: boolean
	}>({ account: false, vehicle: false, upload_docs: false, send_docs: false, opportunity: false })
	const [uploadFiles, setUploadFiles] = useState<File[]>([])
	const [accountModalOpened, setAccountModalOpened] = useState<boolean>(false)
	const [activeKey, setActiveKey] = useState<FunnelStep>('account')
	const [vehicleSearchOpt, setVehicleSearchOpt] = useState<ReactSelectOption | null>(null)
	const [accountSearchOpt, setAccountSearchOpt] = useState<AccountSelectOpt | null>(null)
	const [vehicleSource, setVehicleSource] = useState<string>('hmp')
	const [opportunityFields, setOpportunityFields] = useState<OpportunityField[]>([])
	const [selectedVehicle, setSelectedVehicle] = useState<MotornetSelectedVehicle>({
		brand: null,
		model: null,
		version: null,
	})
	const [vehicleImages, setVehicleImages] = useState<string[]>([])

	const [activateDriveItEasy, setActivateDriveItEasy] = useState<boolean>(false)
	const [opportunityValues, setOpportunityValues] = useState<OpportunityFunnelValues>({
		Tipologia__c: null,
		Canale__c: null,
		Noleggiatore__c: null,
		commissions: '',
		notes: '',
		name: '',
		driveItEasy: false,
		driverEmail: '',
		targa: vehicle?.dettagli_veicolo?.targa || '',
		telaio: vehicle?.dettagli_veicolo?.telaio || '',
		dataFineContrattoDriveItEasy: '',
		nomePartner: null,
	})
	const [referenceUsers, setReferenceUsers] = useState<RemoteSelectOption[]>([]);
	const { showError } = useErrorToast();
	const [searchParams] = useSearchParams();
	const isCreateOptionAction = searchParams?.get('action') === 'create_option';

	useEffect(() => {
		handleApiResponse(apiClient.salesforceAccountController.getReferenceUsers())
			.then((res) => {
				const items: RemoteSelectOption[] = res.map(item => _.isString(item) ? ({
					value: item,
					label: item,
				}) : item);
				setReferenceUsers(items);
			})
			.catch((err) => {
				console.error('Error fetching sales users:', err);
			});
	}, []);

	useEffect(() => {
		const nomePartner = userReferencePartnerList.length > 1
			? null
			: { active: true, label: userReferencePartnerList[0], value: userReferencePartnerList[0] };

		setOpportunityValues((prevValues) => ({
			...prevValues,
			driverEmail: (accountSalesforce?.RecordType?.Name === 'Privato' || accountSalesforce?.RecordType?.Name === 'Libero Professionista')
				? accountSalesforce?.Email__c
				: "",
			nomePartner
		}));
	}, [accountSalesforce, userReferencePartnerList])

	const opprtunityColumns = useMemo(() => {
		return opportunityColumnsData
	}, [])

	const handleActivateDriveItEasy = async () => {
		if (activateDriveItEasy && finalState === OpportunityStageEnum.closed_won) {
			const formattedDateEndDieContract = dayjs(opportunityValues?.dataFineContrattoDriveItEasy).format('DD/MM/YYYY');
			const enableDiePayload: TiassistoVehicleSaveRequestDTO = {
				vehicle: {
					licensePlate: opportunityValues.targa ?? "",
					brand: vehicle?.dati_veicolo?.marca ?? '',
					model: vehicle?.dati_veicolo?.modello ?? '',
					plan_expiration: formattedDateEndDieContract ?? '',
					horizon_point_id: horizonPointId,
				},
				user: {
					firstName: funnelData.account?.FirstName,
					lastName: funnelData.account?.LastName,
					email: opportunityValues?.driverEmail ?? "",
					phone: funnelData.account?.Phone,
					vatNumber: funnelData.account?.Partita_IVA__c ?? null,
					fiscalCode: funnelData.account?.Codice_Fiscale__c ?? '',
					private_client: funnelData.account?.RecordType?.Name === "Azienda" ? 1 : 0,
					ragioneSociale: funnelData.account?.Ragione_Sociale__c,
					commercial_email: funnelData.account?.Owner?.Email ?? '',
					commercial_firstname: funnelData.account?.Owner?.Name ?? ''
				},
				log: {
					operator: user?.username ?? '',
					description: "Inserimento dati veicolo e utente"
				},
			}

			await handleApiResponse(apiClient.tiassistoController.createVehicleUser(enableDiePayload))
		}
	}

	useEffect(() => {
		if (funnelData.account && funnelData.account.Email__c) {
			setSendData({
				...sendData, to: {
					value: funnelData.account.Email__c,
					label: funnelData.account.Email__c
				}
			})
		}
		setCanSave(validateFn())
	}, [funnelData])

	useEffect(() => {
		setCanSave(validateFn())
	}, [sendData, uploadFiles, selectedVehicle, opportunityValues])

	const loadData = async () => {
		if (canChange.includes('opportunity')) {
			// const filters = isCreateOptionAction
			// 	? {
			// 		leadSource: true,
			// 		leadSourceFilter: (item) => OPPORTUNITY_CREATION_LEAD_SOURCE.includes(item.value?.toLowerCase())
			// 			|| OPPORTUNITY_CREATION_LEAD_SOURCE.includes(item.label?.toLowerCase())
			// 	}
			// 	: undefined;
			handleApiResponse(apiClient.salesforceOpportunityController.getOpportunityFields())
				.then(opportunityFieldsRes => {
					let accTyps = dynamicTranslateOpportunityFields(opportunityFieldsRes)
					accTyps.push(
						{
							label: t('dealer'),
							name: 'nomePartner',
							picklistValues: userReferencePartnerList.map(value => ({
								active: true, label: value, value: value
							}))
						}
					)

					accTyps = accTyps.map(field => {
						if (field.name?.toLowerCase() === 'canale__c') {
							const hideSegnalatore = [RoleEnum.SALES_DEALER, RoleEnum.SALES_INTERNAL].includes(userRole) || isMarket(Markets.es);
							if (hideSegnalatore) {
								field.picklistValues = field.picklistValues.filter(
									item => item.value?.toLowerCase() !== 'segnalatore'
								);
							}
						}
						return field;
					})

					const edisonIdx = accTyps?.findIndex(field => field?.name === 'Offerta_Edison__c');

					if (![null, undefined, -1].includes(edisonIdx)) {
						const [edisonField] = accTyps.splice(edisonIdx, 1);
						edisonField && isMarket(Markets.it) && accTyps.push(edisonField);
					}

					setOpportunityFields(accTyps)
				})
		}
		setLoading(false)
	}

	useEffect(() => {
		// ask user tab dismiss confirmation
		// window.onbeforeunload = () => true;

		setActiveKey(startFrom!)

		// Fetch all documents from the folder Noleggiatori/Documentazione from area academy
		findAndSetNoleggiatoriDocuments();

		if (account) {
			setFunnelData({ ...funnelData, account })
		}

		if (vehicle) {
			setVehicleSource('hmp')
		}

		if (mustComplete.includes('send_docs')) {
			setSendData({ ...sendData, enabled: true })
		}

		loadData()
	}, []);

	useEffect(() => {
		setSendData(prevData => ({
			...prevData,
			message: i18next.t('rental_application_instructions_email_template')
		}));
	}, [t]);

	const uppercCaseStrArr = (arr: string[]) => {
		return arr.map(el => el.trim().toUpperCase())
	}

	const loadDealerDocs = async (opt) => {
		if (!funnelData.account) return;

		const folderId = opt.value

		setDealerFolderData([])
		setSendData({ ...sendData, dealer: opt, dealerDocs: [] })

		const folderData = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(folderId))
		let dealerFolderData = folderData.data.filter(el => el.type === 'file');

		const accountType = funnelData.account.RecordType.Name;
		let accountTypeToSearch: { [key: string]: string[] } = {
			'Libero Professionista': uppercCaseStrArr(['liberi professionisti/società di persone']),
			'Privato': uppercCaseStrArr(['Privati']),
			'Azienda': uppercCaseStrArr(['aziende (società di capitali)']),
		};

		const accountTypeFolder = folderData.data.find(el => el.type === 'folder' && accountTypeToSearch[accountType]?.includes(el.title.toUpperCase()))

		if (accountTypeFolder) {
			const accountTypeFolderContent = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(accountTypeFolder.id))
			const accountTypeFolderData = accountTypeFolderContent.data.filter(el => el.type === 'file')
			dealerFolderData = [...dealerFolderData, ...accountTypeFolderData]
		}

		setDealerFolderData(dealerFolderData)
		setSendData({ ...sendData, dealer: opt, dealerDocs: dealerFolderData.map(el => el.title) })
	}

	const findAndSetNoleggiatoriDocuments = async () => {
		const translations = {
			noleggiatori: ['NOLEGGIATORI', 'RENTAL OPERATORS', 'AGENTES DE ALQUILER'],
			documentazione: ['DOCUMENTAZIONE', 'DOCUMENTATION', 'DOCUMENTACIÓN']
		};

		const findFolderByName = (folders, possibleNames) =>
			folders.find(el => possibleNames.includes(el.title.toUpperCase()));

		if (documentationFolder.length === 0) {
			try {
				const res = await handleApiResponse(apiClient.areaAcademyController.getRootAcademyFolder());

				// Find Noleggiatori folder dynamically
				const noleggiatoriFolder = findFolderByName(res.data, translations.noleggiatori);

				if (noleggiatoriFolder) {
					const noleggiatoriFolderRes = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(noleggiatoriFolder.id));


					// Find Documentazione folder dynamically
					const docFolder = findFolderByName(noleggiatoriFolderRes.data, translations.documentazione);

					if (docFolder) {
						const nDocFolderRes = await handleApiResponse(apiClient.areaAcademyController.getSingleFolder(docFolder.id));


						setDocumentationFolder(nDocFolderRes.data
							.filter(el => el.type === 'folder')
							.map(el => ({
								label: el.title,
								value: el.id
							})));
					}
				}
			} catch (err) {
				console.error(err);
			}
		}
	};

	const accountSearch = _.debounce((q: string, cb) => {
		setTimeout(() => {
			handleApiResponse(apiClient.salesforceAccountController.getAccounts({ q }))
				.then(res => {
					let list: AccountEntitySearch[] = res.data;
					const opts: AccountSelectOpt[] = list.map(el => ({
						value: el.Id,
						label: el.Name,
						account: el,
						subtitle: el?.RecordType?.Name || el.Email__c || ''
					}))
					cb(opts)
				})
		})
	}, 500)

	const modelLabel = (model: any) => model?.data?.gammaModello?.descrizione || model?.label || '';

	// Motornet images live on the full vehicle, keyed by the version's eurotax code.
	const fetchImages = async (version: any) => {
		const eurotax = version?.data?.codiceEurotax;
		if (!eurotax) {
			setVehicleImages([]);
			return;
		}
		try {
			const vehicleKind = (selectedVehicle.brand as any)?.data?.vehicleKind ?? 'auto';
			const payload = await apiClient.motornetControllerService.getMotornetVehicle(eurotax, vehicleKind, productionFetchParams(version?.data, selectedVehicle.model?.data));
			setVehicleImages((payload?.images ?? []).map((img: any) => img?.url).filter(Boolean));
		} catch (e) {
			console.warn('Failed to fetch motornet vehicle images', e);
			setVehicleImages([]);
		}
	}

	const vehicleSearch = useMemo(
		() => createVehicleSearch((params) => apiClient.motornetControllerService.getPlatformVehicles(params)),
		[apiClient]
	);

	const setVehicleSearch = (opt: any) => {
		setVehicleSearchOpt(opt)

		setFunnelData({ ...funnelData, vehicle: undefined })

		// FETCH THE VEHICLE SELECTED FROM THE DROPDOWN
		handleApiResponse(apiClient.vehicleController.getVehicles1(opt.vehicle.id))
			.then((res: VehicleEntity) => {
				setFunnelData({ ...funnelData, vehicle: res })
				// Update OpportunityValues targa, telaio and dataFineContrattoDriveItEasy because they are the only values OpportunityValues that are saved into the hmp vehicle. They are needed to autofill OpportunityFunnelDriveItEasyForm component
				setOpportunityValues(prev => ({
					...prev,
					targa: res.dettagli_veicolo?.targa || prev.targa,
					telaio: res.dettagli_veicolo?.telaio || prev.telaio,
					dataFineContrattoDriveItEasy: res.stato_corrente?.dataFineContrattoDriveItEasy || prev.dataFineContrattoDriveItEasy,
				}));
			})
			.catch(error => {
				console.error('Error fetching vehicle details: ', error);
			});
	}

	const getVehicleLabel = (vehicle: VehicleEntity) => {
		return ((vehicle?.dati_veicolo?.marca || '') + ' ' + (vehicle?.dati_veicolo?.modello || ''))
	}

	const getDocumentsCountLabel = (arr: any[]) => {
		return arr.length ? arr.length + ` document${arr.length === 1 ? 'o' : 'i'}` : i18next.t('no_document')
	}

	const isUploadStatusValid = () => {
		if (!mustComplete.includes('upload_docs')) {
			return true;
		}

		if (isCreateOptionAction) {
			return true;
		}

		return uploadFiles.length > 0
	}

	const validateFn = () => {
		let sendDataIsValid = (sendData.enabled) ? (sendData.to !== null && sendData.message.length > 0
			&& sendData.dealerDocs.length > 0) : true;

		let accountStatus = mustComplete.includes('account') ? (funnelData.account !== undefined) : true
		let vehicleStatus = mustComplete.includes('vehicle') ? (funnelData.vehicle !== undefined || selectedVehicle.model !== null) : true
		let uploadStatus = isUploadStatusValid();
		let sendDocsStatus = mustComplete.includes('send_docs') ? sendDataIsValid : true
		const oppSchema = opportunityValues?.entity?.Id
			? baseOpportunitySchema
			: (isCreateOptionAction ? opportunityOptionCreationSchema : baseOpportunitySchema);

		const opportunityValidation = oppSchema.isValidSync(opportunityValues)
		let opportunityStatus = mustComplete.includes('opportunity') ? opportunityValidation : true

		const validation = {
			account: accountStatus,
			vehicle: vehicleStatus,
			upload_docs: uploadStatus,
			send_docs: sendDocsStatus,
			opportunity: opportunityStatus,
		}

		setValidation(validation)

		if ((uploadFiles.length > 0) && nextState === OpportunityStageEnum.waiting_sign) {
			setFinalState(OpportunityStageEnum.credit_score_check)
		}

		if ((uploadFiles.length === 0) && nextState === OpportunityStageEnum.waiting_sign) {
			setFinalState(OpportunityStageEnum.waiting_sign)
		}

		if (userRole === RoleEnum.SALES_DEALER) {
			setFinalState(OpportunityStageEnum.proposal);
		}

		let result = accountStatus && vehicleStatus && sendDataIsValid && uploadStatus && opportunityStatus;

		if (mustComplete.length > 1 && !mustComplete.includes('send_docs') && !mustComplete.includes('upload_docs')) {
			result = result && (sendData.enabled || uploadFiles.length > 0)
		}

		return result
	};

	const uploadFilesToBucket = async () => {
		let filesToUploadList: { filename: string, key: string, file: File, type: string }[] = []

		const uploadContent = {
			'sendFiles': { data: sendFiles },
			'uploadFiles': { data: uploadFiles }
		}

		for (const type in uploadContent) {
			const fileArray: { data: File[], type: 'sendFiles' | 'uploadFiles' } = uploadContent[type]

			if (fileArray.data.length) {
				let uploadOps: any[] = []

				fileArray.data.forEach(file => {
					const ext = file.name.split('.').pop()
					const key = `opportunity_uploads/${randomString(10)}.${ext}`
					filesToUploadList.push({ type, file, filename: file.name, key })
				})

				uploadOps = filesToUploadList.map(obj => {
					return Storage.put(obj.key, obj.file, {
						contentType: obj.file.type,
						bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
					});
				})

				try {
					await Promise.all(uploadOps)
				} catch (e) {
					console.error(e);
					toaster.error(i18next.t('error_loading_file'))
					throw e;
				}
			}
		}

		return filesToUploadList.map(el => {
			return { ...el, key: `public/${el.key}` }
		})
	}

	const hideModal = () => {
		setAccountModalOpened(false)

		if (activeKey === 'opportunity' && validation.opportunity && opportunityValues.Noleggiatore__c) {
			const docFolder = documentationFolder.find(f => f.label === opportunityValues.Noleggiatore__c?.value)

			if (docFolder) {
				loadDealerDocs(docFolder)
			} else {
				setDealerFolderData([])
				setSendData({ ...sendData, dealer: null, dealerDocs: [] })
			}
		}
	};

	const getCommissionsField = () => {
		if ([RoleEnum.MASTER, RoleEnum.BACKOFFICE, RoleEnum.SALES_INTERNAL].includes(userRole)) {
			return 'commissions';
		}

		if ([RoleEnum.AREA_MANAGER, RoleEnum.SALES_DEALER].includes(userRole)) {
			return 'provvigioniPassive';
		}

		return 'commissions';
	};

	const submitFn = async () => {

		if (!validateFn()) return;
		setSending(true);
		let filesToUploadList = await uploadFilesToBucket()

		const selectedOpportunity = opportunityValues.entity || opportunity
		// EDIT MODE OPPORTUNITY
		if (selectedOpportunity) {
			let updateRequest: any = {
				stageName: finalState,
			}

			if (canChange.includes('vehicle')) {
				updateRequest.vehicle = {
					vehicleId: null,
					customVehicle: null,
				}

				if (vehicleSource === 'external') {
					updateRequest.vehicle.customVehicle = {
						brand: getMakeName(selectedVehicle.brand),
						model: modelLabel(selectedVehicle.model),
					}
				} else if (funnelData.vehicle) {
					updateRequest.vehicle.vehicleId = funnelData.vehicle.id
				}
			}

			let vehicleReq = {}
			if (vehicle?.id) {
				vehicleReq = {
					vehicleId: vehicle?.id,
					customVehicle: null
				}
			} else if (canChange.includes('vehicle') && funnelData.vehicle?.id) {
				vehicleReq = {
					vehicleId: funnelData.vehicle?.id,
					customVehicle: null
				}
			} else {
				vehicleReq = {
					vehicleId: selectedOpportunity.ID_Vehicle_HMP__c,
					customVehicle: {
						brand: selectedOpportunity.Brand__c,
						model: selectedOpportunity.Modello__c,
					}
				}
			}

			let updateOpportunityPayload: OpportunityUpdateRequestDTO = {
				name: selectedOpportunity.Name,
				commissions: selectedOpportunity.Provvigioni_canale__c ? strToFloat(selectedOpportunity.Provvigioni_canale__c.toString()) : selectedOpportunity.Provvigioni_canale__c,
				channel: selectedOpportunity.Canale__c,
				type: selectedOpportunity.Tipologia__c,
				dealerName: selectedOpportunity.Noleggiatore__c,
				note: selectedOpportunity.Messaggio__c ?? '',
				vehicle: vehicleReq,
				emailSending: null,
				signedAttachments: null,
				driveItEasy: selectedOpportunity.DriveItEasy__c,
				driverEmail: selectedOpportunity.DriverEmail__c,
				targa: selectedOpportunity.Targa__c,
				telaio: selectedOpportunity.Telaio__c,
				nomePartner: selectedOpportunity.Nome_partner__c,
				offertaEdison: selectedOpportunity.Offerta_Edison__c,
				provvigioniSegnalatore: selectedOpportunity.Provvigioni_Segnalatore__c,
				provvigioniPassive: selectedOpportunity.Provvigioni_passive__c,
			}

			/* SOLO PER HMP ITALIA */
			if (isMarket(Markets.es)) {
				updateOpportunityPayload = _.omit(updateOpportunityPayload, ['offertaEdison'])
			}

			console.debug('[OpportunityFunnel]', { vehicle, funnelData, selectedOpportunity, vehicleReq, updateOpportunityPayload, updateRequest })

			if (canChange.includes('upload_docs') && uploadFiles.length) {
				const attachments = filesToUploadList.filter(el => el.type === 'uploadFiles')
					.map(item => {
						return {
							"filename": item.filename,
							"key": item.key
						}
					})
				updateOpportunityPayload.signedAttachments = attachments
			}


			if (canChange.includes('send_docs')) {
				if (sendData.enabled) {
					updateOpportunityPayload.emailSending = {
						"to": sendData.to!.value,
						"cc": sendData.cc.map(el => el.value),
						"body": sendData.message,
						"existingAttachments": sendData.dealerDocs.map(el => {
							return { key: 'public/' + el, filename: el }
						}),
						"uploadedAttachments": filesToUploadList.filter(el => el.type === 'sendFiles').map(item => {
							return {
								"filename": item.filename,
								"key": item.key
							}
						}),
					}
				}
			}

			// UPDATE VEHICLE HMP
			if (
				opportunityValues.targa !== vehicle?.dettagli_veicolo?.targa ||
				opportunityValues.telaio !== vehicle?.dettagli_veicolo?.telaio ||
				opportunityValues.dataFineContrattoDriveItEasy !== vehicle?.stato_corrente?.dataFineContrattoDriveItEasy
			) {
				// Update only the fields that changed
				const updatedStatoCorrente = {
					...vehicle?.stato_corrente,
					dataFineContrattoDriveItEasy: opportunityValues.dataFineContrattoDriveItEasy || vehicle?.stato_corrente?.dataFineContrattoDriveItEasy,
				};

				const updateVehiclePayload: VehicleUpdateRequestDTO = {
					// Include all fields from the DTO with existing values
					id_bo: vehicle?.dettagli_veicolo?.id_bo,
					city: vehicle?.dettagli_veicolo?.city,
					colore_esterno: vehicle?.dettagli_veicolo?.colore_esterno,
					colore_interno: vehicle?.dettagli_veicolo?.colore_interno,
					data_disp: vehicle?.dettagli_veicolo?.data_disp,
					data_immatricolazione: vehicle?.dettagli_veicolo?.data_immatricolazione,
					data_telaio: vehicle?.dettagli_veicolo?.data_telaio,
					giorno_disp: vehicle?.dettagli_veicolo?.giorno_disp,
					note: vehicle?.dettagli_veicolo?.note,
					optionals: vehicle?.optionals,
					promo: vehicle?.dettagli_veicolo?.promo,
					promo_note: vehicle?.dettagli_veicolo?.promo_note,
					settimana_prod: vehicle?.dettagli_veicolo?.settimana_prod,
					targa: opportunityValues.targa || vehicle?.dettagli_veicolo?.targa,
					telaio: opportunityValues.telaio || vehicle?.dettagli_veicolo?.telaio,
					document: vehicle?.dettagli_veicolo?.document,
					stato_corrente: updatedStatoCorrente,
					prezzo_listino: vehicle?.dati_veicolo?.prezzo_listino,
					in_evidenza: vehicle?.dettagli_veicolo?.in_evidenza,
					lista_noleggiatori_quotatore: vehicle?.lista_noleggiatori_quotatore,
					extra_sconto_perc: vehicle?.dati_veicolo?.extra_sconto_perc,
					limited_visibility: vehicle?.dettagli_veicolo?.limited_visibility || [],
					// Mandatory fields
					codice_costruttore: vehicle?.dettagli_veicolo?.codice_costruttore ?? '',
					codice_colore_esterno: vehicle?.dettagli_veicolo?.codice_colore_esterno ?? '',
					codice_colore_interno: vehicle?.dettagli_veicolo?.codice_colore_interno ?? '',
					dealer: vehicle?.dettagli_veicolo?.dealer ?? '',
					nr_ordine: vehicle?.dettagli_veicolo?.nr_ordine ?? '',
				};


				try {
					vehicle?.id && await handleApiResponse(apiClient.vehicleController.putVehicles(vehicle?.id as string, updateVehiclePayload))
				} catch (err) {
					console.error(err)
					toaster.error(i18next.t('error_updating_vehicle'))
				}
			}


			// UPDATE OPPORTUNITY
			try {
				updateOpportunityPayload = { ...updateOpportunityPayload, ...updateRequest }
				await handleApiResponse(apiClient.salesforceOpportunityController.editOpportunity(selectedOpportunity.Id, updateOpportunityPayload))
				await handleActivateDriveItEasy()

				if (afterSubmitFn) {
					afterSubmitFn(selectedOpportunity.Id)
				}
			} catch (err) {
				console.error(err)
				showError(err, i18next.t('error_loading_data'))
			} finally {
				setSending(false)
			}
		} else {

			// NEW OPPORTUNITY MODE
			const isMasterOrBackoffice = [RoleEnum.MASTER, RoleEnum.BACKOFFICE].includes(userRole);
			const isSegnalazione = opportunityValues?.Canale__c?.value?.toLowerCase() === 'segnalatore';
			const commissionsField = getCommissionsField();

			const provvPayload = isMasterOrBackoffice
				? {
					commissions: opportunityValues?.commissions
						? strToFloat(opportunityValues.commissions)
						: 0,
					provvigioniPassive: opportunityValues?.provvigioniPassive
						? strToFloat(opportunityValues.provvigioniPassive)
						: undefined,
				  }
				: { [commissionsField]: strToFloat(opportunityValues?.commissions) };

			// @ts-ignore
			let createOpportunityPayload: OpportunityCreateRequestDTO = {
				accountId: funnelData.account!.Id,
				...provvPayload,
				provvigioniSegnalatore: isSegnalazione && opportunityValues?.provvigioniSegnalatore
					? strToFloat(opportunityValues.provvigioniSegnalatore)
					: undefined,
				segnalatore: isSegnalazione ? (opportunityValues?.segnalatore || undefined) : undefined,
				Ragione_Sociale_Segnalatore__c: isSegnalazione ? (opportunityValues?.ragioneSocialeSegnalatore || undefined) : undefined,
				channel: opportunityValues?.Canale__c?.value,
				type: opportunityValues?.Tipologia__c?.value,
				name: `HMP-${funnelData.account!.Name}-${opportunityValues?.name}`,
				dealerName: opportunityValues?.Noleggiatore__c?.value,
				emailSending: null,
				signedAttachments: null,
				note: opportunityValues?.notes,
				stageName: finalState,
				vehicle: {
					vehicleId: null,
					customVehicle: null
				},
				driveItEasy: opportunityValues?.driveItEasy,
				driverEmail: opportunityValues?.driverEmail,
				targa: opportunityValues?.targa,
				telaio: opportunityValues?.telaio,
				isConvertedFromLead__c: false,
				nomePartner: opportunityValues?.nomePartner?.value,
				Offerta_Edison__c: opportunityValues?.Offerta_Edison__c?.value,
				salesUser: opportunityValues?.salesUser || '',
				referenceUserId: opportunityValues?.referenceUserId || '',
				backupUsers: opportunityValues?.backupUsers || [],
				LeadSource: opportunityValues?.LeadSource?.value,
			}

			/* SOLO PER HMP ITALIA */
			if (isMarket(Markets.es)) {
				createOpportunityPayload = _.omit(createOpportunityPayload, ['Offerta_Edison__c'])
			}

			if (canChange.includes('vehicle')) {
				if (vehicleSource === 'external') {
					createOpportunityPayload.vehicle!.customVehicle = {
						brand: getMakeName(selectedVehicle.brand),
						model: modelLabel(selectedVehicle.model),
					};
				} else if (funnelData.vehicle) {
					createOpportunityPayload.vehicle!.vehicleId = funnelData.vehicle.id?.toString() || null;
				}
			} else {
				if (funnelData.vehicle) {
					createOpportunityPayload.vehicle!.vehicleId = funnelData.vehicle.id?.toString() || null;
				}
			}

			if (canChange.includes('send_docs')) {
				if (sendData.enabled) {
					createOpportunityPayload.emailSending = {
						"to": sendData.to!.value,
						"cc": sendData.cc.map(el => el.value),
						"body": sendData.message,
						"existingAttachments": sendData.dealerDocs.map(el => {
							return { key: 'public/' + el, filename: el }
						}),
						"uploadedAttachments": filesToUploadList.filter(el => el.type === 'sendFiles').map(item => {
							return {
								"filename": item.filename,
								"key": item.key
							}
						}),
					}
				}
			}

			if (canChange.includes('upload_docs') && uploadFiles.length) {
				createOpportunityPayload.signedAttachments = filesToUploadList.filter(el => el.type === 'uploadFiles').map(item => {
					return {
						"filename": item.filename,
						"key": item.key
					}
				})
			}

			// UPDATE VEHICLE HMP only if targa or telatio or dataFineContrattoDriveItEasy are changed by user input
			if (
				funnelData.vehicle?.id && (
					opportunityValues.targa !== funnelData.vehicle?.dettagli_veicolo?.targa ||
					opportunityValues.telaio !== funnelData.vehicle?.dettagli_veicolo?.telaio ||
					opportunityValues.dataFineContrattoDriveItEasy !== funnelData.vehicle?.stato_corrente?.dataFineContrattoDriveItEasy
				)
			) {
				// Update only the fields that changed
				const updatedStatoCorrente = {
					...funnelData.vehicle?.stato_corrente,
					dataFineContrattoDriveItEasy: opportunityValues.dataFineContrattoDriveItEasy || funnelData.vehicle?.stato_corrente?.dataFineContrattoDriveItEasy,
				};

				const updateVehiclePayload: VehicleUpdateRequestDTO = {
					// Include all fields from the DTO with existing values
					id_bo: funnelData.vehicle?.dettagli_veicolo?.id_bo,
					city: funnelData.vehicle?.dettagli_veicolo?.city,
					colore_esterno: funnelData.vehicle?.dettagli_veicolo?.colore_esterno,
					colore_interno: funnelData.vehicle?.dettagli_veicolo?.colore_interno,
					data_disp: funnelData.vehicle?.dettagli_veicolo?.data_disp,
					data_immatricolazione: funnelData.vehicle?.dettagli_veicolo?.data_immatricolazione,
					data_telaio: funnelData.vehicle?.dettagli_veicolo?.data_telaio,
					giorno_disp: funnelData.vehicle?.dettagli_veicolo?.giorno_disp,
					note: funnelData.vehicle?.dettagli_veicolo?.note,
					optionals: funnelData.vehicle?.optionals,
					promo: funnelData.vehicle?.dettagli_veicolo?.promo,
					promo_note: funnelData.vehicle?.dettagli_veicolo?.promo_note,
					settimana_prod: funnelData.vehicle?.dettagli_veicolo?.settimana_prod,
					targa: opportunityValues.targa || funnelData.vehicle?.dettagli_veicolo?.targa,
					telaio: opportunityValues.telaio || funnelData.vehicle?.dettagli_veicolo?.telaio,
					document: funnelData.vehicle?.dettagli_veicolo?.document,
					stato_corrente: updatedStatoCorrente,
					prezzo_listino: funnelData.vehicle?.dati_veicolo?.prezzo_listino,
					in_evidenza: funnelData.vehicle?.dettagli_veicolo?.in_evidenza,
					lista_noleggiatori_quotatore: funnelData.vehicle?.lista_noleggiatori_quotatore,
					extra_sconto_perc: funnelData.vehicle?.dati_veicolo?.extra_sconto_perc,
					limited_visibility: funnelData.vehicle?.dettagli_veicolo?.limited_visibility || [],
					// Mandatory fields
					codice_costruttore: funnelData.vehicle?.dettagli_veicolo?.codice_costruttore ?? '',
					codice_colore_esterno: funnelData.vehicle?.dettagli_veicolo?.codice_colore_esterno ?? '',
					codice_colore_interno: funnelData.vehicle?.dettagli_veicolo?.codice_colore_interno ?? '',
					dealer: funnelData.vehicle?.dettagli_veicolo?.dealer ?? '',
					nr_ordine: funnelData.vehicle?.dettagli_veicolo?.nr_ordine ?? '',
				};


				try {
					await handleApiResponse(apiClient.vehicleController.putVehicles(funnelData.vehicle?.id as string, updateVehiclePayload))
				} catch (err) {
					console.error(err)
					toaster.error(i18next.t('error_updating_vehicle'))
				}
			}

			// CREATE OPPORTUNITY
			try {
				const res = await handleApiResponse(apiClient.salesforceOpportunityController.createOpportunity(createOpportunityPayload))
				await handleActivateDriveItEasy()

				if (afterSubmitFn) {
					afterSubmitFn(res.id)
				}
			} catch (err) {
				console.error(err)
				showError(err, i18next.t('error_loading_data'))
			} finally {
				setSending(false)
			}
		}
	};

	const cleanOpportunityValues = () => {
		setOpportunityValues({
			entity: null,
			Tipologia__c: null,
			Canale__c: null,
			Noleggiatore__c: null,
			commissions: '',
			notes: '',
			name: '',
			driveItEasy: false,
			driverEmail: "",
			targa: "",
			telaio: "",
			dataFineContrattoDriveItEasy: "",
			nomePartner: null,
		})
	}

	const handleDoubleClick = async (opportunity: OpportunityEntity) => {
		let backofficeData: OpportunityBackofficeDTO | null = null;

		try {
			backofficeData = await handleApi(apiClient.salesforceOpportunityController.getOpportunityBackoffice(opportunity.Id));
		} catch (error) {
			console.warn('failed to get backoffice data for opportunity ' + opportunity?.Id, error)
		}


		const noleggiatore = opportunity.Noleggiatore__c ?? ''
		const tipologia = opportunity.Tipologia__c ?? ''
		const canale = opportunity.Canale__c ?? ''
		const refUser = referenceUsers?.find(item => item?.value === opportunity.OwnerId);

		let oppValues = {
			Canale__c: { value: canale, label: canale },
			Noleggiatore__c: { value: noleggiatore, label: noleggiatore },
			Tipologia__c: { value: tipologia, label: tipologia },
			notes: opportunity.Messaggio__c ?? '',
			name: opportunity.Name,
			commissions: opportunity.Provvigioni_canale__c?.toString() || opportunity.Provvigioni_passive__c?.toString(),
			entity: opportunity,
			driveItEasy: opportunity.DriveItEasy__c ?? false,
			driverEmail: opportunity.DriverEmail__c ?? '',
			targa: opportunity.Targa__c || vehicle?.dettagli_veicolo?.targa || '',
			telaio: opportunity.Telaio__c || vehicle?.dettagli_veicolo?.telaio || '',
			dataFineContrattoDriveItEasy: vehicle?.stato_corrente?.dataFineContrattoDriveItEasy ?? '',
			nomePartner: opportunity.Nome_partner__c ?? '',
			salesUser: opportunity.Owner_HMP__c,
			referenceUserId: opportunity.OwnerId,
			referenceUserLabel: refUser?.label,
			backupUsers: backofficeData?.backupUsers || [],
			provvigioniSegnalatore: opportunity.Provvigioni_Segnalatore__c,
			provvigioniPassive: opportunity.Provvigioni_passive__c,
		}

		setOpportunityValues(oppValues)
	}

	return (
		<div>
			<Nav fill variant="tabs" activeKey={activeKey} onSelect={(k) => {
				setActiveKey(k as FunnelStep || i18next.t('account'))
			}}>
				<NavItem
					eventKey="account"
					title={i18next.t('account')}
					isValid={validation.account}
					mustComplete={mustComplete}
					text={funnelData.account?.Name || i18next.t('select_one_account')} />
				{canChange.includes('opportunity') && (
					<NavItem
						eventKey="opportunity"
						title={i18next.t('opportunity_data')}
						isValid={validation.opportunity}
						mustComplete={mustComplete}
						text={validation.opportunity ? i18next.t('completed') : i18next.t('complete_form')} />
				)}
				<NavItem
					eventKey="vehicle"
					title={i18next.t('vehicle')}
					isValid={validation.vehicle}
					mustComplete={mustComplete}
					showIsOptional={false}
					text={funnelData.vehicle ? getVehicleLabel(funnelData.vehicle) :
						(selectedVehicle.model ? `${getMakeName(selectedVehicle?.brand)} ${modelLabel(selectedVehicle.model)}` : 'Seleziona un veicolo')} />
				{canChange.includes('send_docs') && (
					<NavItem
						eventKey="send_docs"
						title={i18next.t('send_quote_docs')}
						forceRequired={mustComplete.includes('send_or_upload_docs') && !(uploadFiles.length > 0 && validation.upload_docs)}
						mustComplete={mustComplete}
						isValid={sendData.enabled ? validation.send_docs : false}
						showIsOptional={false}
						text={getDocumentsCountLabel([...sendFiles, ...sendData.dealerDocs])}></NavItem>
				)}
				{canChange.includes('upload_docs') && (
					<NavItem
						eventKey="upload_docs"
						title={i18next.t('send_upload_quote')}
						forceRequired={mustComplete.includes('send_or_upload_docs') && !(sendData.enabled ? validation.send_docs : false)}
						mustComplete={mustComplete}
						showIsOptional={isCreateOptionAction ? true : false}
						text={getDocumentsCountLabel(uploadFiles)}
						isValid={(uploadFiles.length > 0 && validation.upload_docs)}
					></NavItem>
				)}
			</Nav>
			{!loading ? (
				<div>
					<Card>
						<Card.Body className='pt-4'>

							{/* ACCOUNT CONTENT */}
							{activeKey === 'account' && (
								<>
									{(
										<div>
											{canChange.includes('account') && (
												<Row>
													{accountSearchOpt && !accountSearchOpt.account.RecordType && (
														<Col md={12}>
															<MissingAccountRecordTypeAlert accountId={accountSearchOpt.account.Id} />
														</Col>
													)}
													<Col md={6}>
														<div className="mb-3">
															<Button
																onClick={() => {
																	setAccountModalOpened(true)
																	setModalType('account')
																}}
																disabled={funnelData.account !== undefined}>
																+ {t('create_new_account')}
															</Button>
														</div>
														<Form.Group>
															<AsyncSelect
																menuPosition="fixed"
																isClearable
																isDisabled={!canChange.includes('account')}
																placeholder={t('select_one_account')}
																value={accountSearchOpt}
																noOptionsMessage={() => i18next.t('type_to_search')}
																defaultOptions={accountSearchOpt ? [accountSearchOpt] : []}
																loadOptions={accountSearch}
																onChange={(opt: any) => {
																	setAccountSearchOpt(opt)
																	setAccountSalesforce(opt?.account)
																	setFunnelData({ ...funnelData, account: opt?.account?.RecordType ? opt.account : undefined })
																}}
																components={{
																	Option: (props: any) => {
																		return <SelectOptWithSubtitle	 {...props}></SelectOptWithSubtitle>
																	}
																}}
															/>
														</Form.Group>
													</Col>
												</Row>
											)}

											{funnelData.account && (
												<Row className='mt-3'>
													<Col md={12}>
														{<AccountSmallPreview
															account={funnelData.account}
															showAccountBtn={account === null}
														/>}
													</Col>
												</Row>
											)}
										</div>
									)}
								</>
							)}

							{/* OPPORTUNITY CONTENT */}
							{activeKey === 'opportunity' && (
								<div>
									{funnelData.account ? (
										<Form autoComplete='off' onSubmit={() => { }}>
											<Row>
												<Col md={6}>
													<div>
														<Button
															onClick={() => {
																validateFn()

																if (validation.opportunity) {
																	cleanOpportunityValues()
																	// validateFn()
																} else {
																	setAccountModalOpened(true)
																	setModalType('opportunity')
																}
															}}
															disabled={funnelData.account === undefined}>
															{validation.opportunity ? t('change') : `+ ${t('create_new')}`}
														</Button>
														{validation.opportunity && !opportunityValues.entity && canChange.includes('opportunity') && (
															<Button
																variant='secondary'
																className='m-2'
																onClick={() => {
																	setAccountModalOpened(true)
																	setModalType('opportunity')
																}}>
																{t('edit')}
															</Button>
														)}
													</div>
												</Col>

												<Col md={12}>
													<div>
														{!accountModalOpened && validation.opportunity && (
															<div className='mt-3'>
																<OpportunitySmallPreview opportunity={opportunityValues} />
															</div>
														)}

														{vehicle && !validation.opportunity && (
															<div>
																<div className="mt-4">
																	<b>{t('opportunities_created_from_hmp')}</b>
																	{!accountModalOpened && !validation.opportunity && (
																		<div>
																			{/* STATO ATTESA FIRMA */}
																			{nextState === OpportunityStageEnum.waiting_sign && (
																				<OpportunitiesTable
																					customColumnsData={opprtunityColumns}
																					dblClickCallback={handleDoubleClick}
																					query={{
																						vehicleId: vehicle.id!.toString(),
																						accountId: funnelData.account.Id,
																						stageName: [OpportunityStageEnum.proposal, OpportunityStageEnum.qualification, OpportunityStageEnum.negotiation]
																					}}
																				/>
																			)}

																			{/* STATO ATTESA CONFERMA CREDITO */}
																			{nextState === OpportunityStageEnum.credit_score_check && (
																				<OpportunitiesTable
																					customColumnsData={opprtunityColumns}
																					dblClickCallback={handleDoubleClick}
																					query={{
																						vehicleId: vehicle.id!.toString(),
																						accountId: funnelData.account.Id,
																						stageName: [OpportunityStageEnum.proposal, OpportunityStageEnum.qualification, OpportunityStageEnum.negotiation, OpportunityStageEnum.waiting_sign]
																					}}
																				/>
																			)}

																			{/* STATO CHIUSA VINTA */}
																			{nextState === OpportunityStageEnum.closed_won && (
																				<OpportunitiesTable
																					customColumnsData={opprtunityColumns}
																					dblClickCallback={handleDoubleClick}
																					query={{
																						vehicleId: vehicle.id!.toString(),
																						accountId: funnelData.account.Id,
																						stageName: [OpportunityStageEnum.proposal, OpportunityStageEnum.qualification, OpportunityStageEnum.negotiation,
																						OpportunityStageEnum.waiting_sign, OpportunityStageEnum.credit_score_check]
																					}}
																				/>
																			)}
																		</div>
																	)}
																</div>

																<div className="mt-5">
																	<b>{t('opportunity_without_a_vehicle_associated')}</b>
																	{!accountModalOpened && !validation.opportunity && (
																		<OpportunitiesTable
																			customColumnsData={opprtunityColumns}
																			dblClickCallback={handleDoubleClick}
																			query={{
																				withoutHmpVehicle: true,
																				accountId: funnelData.account.Id,
																				stageName: [OpportunityStageEnum.proposal, OpportunityStageEnum.qualification, OpportunityStageEnum.negotiation,
																				OpportunityStageEnum.waiting_sign, OpportunityStageEnum.credit_score_check, OpportunityStageEnum.suspended]
																			}}
																		/>
																	)}
																</div>
															</div>
														)}
													</div>
												</Col>
											</Row>
										</Form>
									) :
										<Alert variant='secondary' className="mb-4">
											{t('account_selection_required_to_proceed')}
										</Alert>
									}
								</div>
							)}

							{/* VEHICLE SECTION OPPORTUNITY */}
							{activeKey === 'vehicle' && (
								<div>
									{canChange.includes('vehicle') && (
										<div>
											<Row className='mb-3'>
												<Nav variant='tabs' activeKey={vehicleSource} onSelect={(k) => {
													setFunnelData({ ...funnelData, vehicle: undefined })
													setVehicleSearchOpt(null)
													setSelectedVehicle({ brand: null, model: null, version: null })
													setVehicleSource(k || 'hmp')
												}}>
													<Nav.Item>
														<Nav.Link eventKey="hmp">	{t('vehicle_hmp')}</Nav.Link>
													</Nav.Item>
													<Nav.Item>
														<Nav.Link eventKey="external">	{t('vehicle_external')}</Nav.Link>
													</Nav.Item>
												</Nav>
											</Row>

											{/* SELECT VEHICLE */}
											<Row>
												{vehicleSource === 'hmp' && (
													<Col md={6}>
														<Form.Group>
															<AsyncSelect
																menuPosition="fixed"
																placeholder={i18next.t('select')}
																value={vehicleSearchOpt}
																noOptionsMessage={() => {
																	return i18next.t('vehicle_search_placeholder', { defaultValue: 'Ordine, telaio, targa o codice costruttore' })
																}}
																defaultOptions={vehicleSearchOpt ? [vehicleSearchOpt] : []}
																loadOptions={vehicleSearch}
																onChange={setVehicleSearch}
																components={{
																	Option: (props: any) => {
																		return <SelectOptWithSubtitle {...props}></SelectOptWithSubtitle>
																	}
																}}
															/>
														</Form.Group>
													</Col>
												)}

												{vehicleSource === 'external' && (
													<MotornetVehicleSelector
														selectedVehicle={selectedVehicle}
														onBrandChange={(brand) => {
															setSelectedVehicle({ brand, model: null, version: null })
															setVehicleImages([])
														}}
														onModelChange={(model) => {
															setSelectedVehicle((prev) => ({ ...prev, model, version: null }))
															setVehicleImages([])
														}}
														onVersionChange={(version) => {
															setSelectedVehicle((prev) => ({ ...prev, version }))
															fetchImages(version)
														}}
													/>
												)}
											</Row>
										</div>
									)}

									<Card className='mt-4'>
										{funnelData.vehicle ? (
											<VehicleCardBody data={funnelData.vehicle} />
										) : (vehicleImages.length > 0 ?
											<VehicleCardBody data={{
												dati_veicolo: {
													marca: getMakeName(selectedVehicle.brand),
													modello: modelLabel(selectedVehicle.model)
												},
												image_list: vehicleImages.map(el => {
													return { url: el }
												})
											}} />
											: null)}
									</Card>

									{canChange.includes('opportunity') && (
										<TiassistoElement>
											<OpportunityFunnelDriveItEasyForm {...{ finalState, opportunityValues, setOpportunityValues, activateDriveItEasy, setActivateDriveItEasy }} />
										</TiassistoElement>
									)}
								</div>
							)}

							{/* SEND DOCS CONTENT */}
							{activeKey === 'send_docs' && (
								<div>
									{!funnelData.account ? (
										<Alert>
											{t('account_selection_required_to_proceed')}
										</Alert>
									) : (
										<div>
											<Row className='my-2'>
												<Col>
													<Form.Group>
														<Form.Check
															type="switch"
															name='send-docs-switch'
															label={i18next.t('send_documents_to_customer_for_signature')}
															checked={sendData.enabled}
															disabled={mustComplete.includes('send_docs')}
															onChange={({ target }) => {
																const enabled = target.checked
																if (!target.checked) {
																	setDealerFolderData([])
																	setSendData({ ...sendData, dealer: null, dealerDocs: [], enabled })
																} else {
																	setSendData({ ...sendData, enabled })
																}
															}}
														/>
													</Form.Group>
												</Col>
											</Row>

											{sendData.enabled && (
												<div className='mt-4'>
													<Row>
														<Col md={4}>
															<Select
																menuPosition="fixed"
																options={documentationFolder}
																placeholder={i18next.t('select_rental_company')}
																noOptionsMessage={() => `${i18next.t('loading')}...`}
																value={sendData.dealer}
																onChange={(opt: any) => {
																	loadDealerDocs(opt)
																}}
															/>
															{/* Dealer docs checkbox */}
															{dealerFolderData.length > 0 && (
																<div className='mt-3'>
																	{dealerFolderData.map(el => {
																		return (
																			<Form.Check
																				key={el.id}
																				label={el.title}
																				name="dealer-docs"
																				type={'checkbox'}
																				value={el.title}
																				checked={sendData.dealerDocs.includes(el.title)}
																				onChange={({ target: { checked, value } }) => {
																					const docs = new Set(sendData.dealerDocs);
																					checked ? docs.add(value) : docs.delete(value)

																					setSendData({ ...sendData, dealerDocs: Array.from(docs) })
																				}}
																			/>
																		)
																	})}
																</div>
															)}
														</Col>
													</Row>

													<div className="my-4"><hr /></div>

													<Row >
														<Col md={6}>
															<CreatableSelect
																isClearable
																components={{ DropdownIndicator: null }}
																placeholder="A"
																options={sendData.to ? [sendData.to] : []}
																noOptionsMessage={() => i18next.t('add_customer_email')}
																value={sendData.to}
																formatCreateLabel={(i) => emailIsValid(i) ? `Invia a ${i}` : `${i}`}
																onChange={(opt: any) => {
																	if (!opt) setSendData({ ...sendData, to: opt })
																	else if (emailIsValid(opt.value)) {
																		setSendData({ ...sendData, to: opt })
																	}
																}}
															></CreatableSelect>
														</Col>
														<Col md={6}>
															<CreatableSelect
																isMulti
																components={{ DropdownIndicator: null }}
																placeholder="C.c."
																noOptionsMessage={() => i18next.t('add_cc_recipients')}
																value={sendData.cc}
																formatCreateLabel={(i) => emailIsValid(i) ? `Aggiungi ${i}` : `${i}`}
																onChange={(opt: any) => {
																	opt = opt.filter(el => emailIsValid(el.value))
																	setSendData({ ...sendData, cc: removeDuplicates(opt) })
																}}
															></CreatableSelect>
														</Col>
													</Row>
													<Row className='mt-2'>
														<Col xs={12}>
															<ReactQuill theme="snow"
																placeholder={i18next.t('write_email')}
																value={sendData.message} onChange={(message) => {
																	setSendData({ ...sendData, message: message })
																}} />
														</Col>
													</Row>
													<div className="mt-4">
														<MultiFileUploader
															files={sendFiles}
															setFiles={setsendFiles}
															types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
														></MultiFileUploader>
													</div>
												</div>
											)}
										</div>
									)}
								</div>
							)}

							{/* UPLOAD DOCS CONTENT */}
							{activeKey === 'upload_docs' && (
								<div>
									<div className="">
										<p className='text-muted text-center'>
											<b>
												{t('upload_rental_proposal_or_signed_documents')}
												<br />
												{/* L'opportunity specificata verrà salvata in stato "{nextState === OpportunityState.waiting_sign ? OPPORTUNITY_STATES_DATA[OpportunityState.credit_score_check].label : OPPORTUNITY_STATES_DATA[finalState].label}" */}
											</b>
										</p>
										<MultiFileUploader
											files={uploadFiles}
											setFiles={setUploadFiles}
											types={OPPORTUNITY_SUPPORTED_FILE_TYPES}
										></MultiFileUploader>
									</div>
								</div>
							)}

						</Card.Body>
					</Card>

					<div className="mt-4 text-end">
						<div>
							<Button variant='Link' onClick={onQuit} disabled={sending}>{t('close')}</Button>
							<Button variant='primary' onClick={() => submitFn()} disabled={!canSave || sending}>
								{sending ? <Spinner animation='border' size='sm' /> : sendData.enabled ? i18next.t('save_and_send') : i18next.t('save')}
							</Button>
						</div>
						<div>
							<hr />
							<small className="text-muted">
								<FaInfoCircle /> {t('opportunity_saved_as')} <b>"{translateOpportunityState(OPPORTUNITY_STATES_DATA[finalState].label)}"</b>
							</small>
						</div>
					</div>
				</div>
			) : (
				<CardPlaceholder />
			)}

			{/* TODO: da sistemare con context o redux */}
			<OpportunityModals
				validation={validation}
				modalType={modalType}
				setAccountSearchOpt={setAccountSearchOpt}
				setAccountModalOpened={setAccountModalOpened}
				setFunnelData={setFunnelData}
				funnelData={funnelData}
				show={accountModalOpened}
				onHide={hideModal}
				account={account}
				setOpportunityValues={setOpportunityValues}
				opportunityValues={opportunityValues}
				opportunityFields={opportunityFields}
				opportunityValidationSchema={isCreateOptionAction ? opportunityOptionCreationSchema : undefined}
			/>
		</div >
	);
};

export default OpportunityFunnel;
